import React, { Fragment } from 'react'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../../styled'
import {
  Application,
  AttackEventDetails,
  getUserInputValue,
  StackFrame,
  UserInput
} from '../shared'
import {
  AttackEventCodeLocation,
  AttackEventSuspiciousQueryElements,
  PossibleFindings
} from '../attack-event-details'
import {
  ChainedCommandsDetailsExamples,
  DangerousPathsExploitsDetailsExamples,
  ParameterBackdoorsDetailsExamples,
  ProcessHardeningDetailsExamples
} from '../attack-event-details-examples'
import { CodeSnippet } from '../../../legacy-design-system/code-snippet'
import { WithRedactedTooltip } from '../with-redacted-tooltip'
import { FormattedMessage } from 'react-intl'

const possibleFindings: PossibleFindings = {
  CHAINING: ChainedCommandsDetailsExamples,
  BACKDOOR: ParameterBackdoorsDetailsExamples,
  PATH_ARGUMENT: DangerousPathsExploitsDetailsExamples
}

export interface AttackEventDetailsCommandInjectionProps {
  readonly application: Application
  readonly userInput?: UserInput
  readonly details: AttackEventDetails
  readonly file?: string
  readonly method?: string
  readonly line?: number
  readonly stackFrames?: StackFrame[]
  readonly lastCustomFrame?: number
}

export interface ExamplesComponent {
  readonly WrappedExamplesComponent: React.ComponentType<AttackEventDetailsCommandInjectionProps>
}

export function AttackEventDetailsCommandInjectionWrapper(
  props: AttackEventDetailsCommandInjectionProps & ExamplesComponent
) {
  const {
    file,
    method,
    line,
    stackFrames,
    userInput,
    WrappedExamplesComponent,
    lastCustomFrame
  } = props

  return (
    <Fragment>
      <AttackEventCodeLocation
        file={file}
        method={method}
        line={line}
        stackFrames={stackFrames}
        lastCustomFrame={lastCustomFrame}
      />
      {userInput && (
        <CodeSnippet
          labelId="title_attackEventDetails_suspiciousCommand"
          className={css`
            color: ${theme.color.red50};
            margin-bottom: 1.5em;
          `}
          labelClassName={css`
            font-weight: ${deprecatedTheme.fontWeights.primary.bold};
          `}
        >
          <WithRedactedTooltip text={getUserInputValue(userInput)} />
        </CodeSnippet>
      )}

      <WrappedExamplesComponent {...props} />
    </Fragment>
  )
}

export function AttackEventDetailsCommandInjection(
  props: AttackEventDetailsCommandInjectionProps
) {
  return (
    <AttackEventDetailsCommandInjectionWrapper
      {...props}
      WrappedExamplesComponent={(
        props: AttackEventDetailsCommandInjectionProps
      ) => {
        return (
          <React.Fragment>
            {Array.isArray(props.details.findings) && (
              <AttackEventSuspiciousQueryElements
                language={props.application.language}
                labelId="variable_attackEventDetails_commandContainsSuspiciousElements"
                findings={props.details.findings}
                possibleFindings={possibleFindings}
              />
            )}
          </React.Fragment>
        )
      }}
    />
  )
}

const renderSingleExample = (component) => {
  return (
    <Fragment>
      <span
        className={css`
          color: ${theme.color.neutral100};
        `}
      >
        <FormattedMessage
          id="variable_attackEventDetails_commandContainsSuspiciousElements"
          values={{ count: 1 }}
        />
      </span>
      {component}
    </Fragment>
  )
}

export const AttackEventDetailsCommandInjectionChainedCommands = (
  props: AttackEventDetailsCommandInjectionProps
) => {
  return (
    <AttackEventDetailsCommandInjectionWrapper
      {...props}
      WrappedExamplesComponent={({ application }) =>
        renderSingleExample(
          <ChainedCommandsDetailsExamples
            language={application.language}
            hideDetectionText={true}
          />
        )
      }
    />
  )
}

export const AttackEventDetailsCommandInjectionCommandBackdoors = (
  props: AttackEventDetailsCommandInjectionProps
) => {
  return (
    <AttackEventDetailsCommandInjectionWrapper
      {...props}
      WrappedExamplesComponent={({ application }) =>
        renderSingleExample(
          <ParameterBackdoorsDetailsExamples
            language={application.language}
            hideDetectionText={true}
          />
        )
      }
    />
  )
}

export const AttackEventDetailsCommandInjectionDangerousPaths = (
  props: AttackEventDetailsCommandInjectionProps
) => {
  return (
    <AttackEventDetailsCommandInjectionWrapper
      {...props}
      WrappedExamplesComponent={({ application }) =>
        renderSingleExample(
          <DangerousPathsExploitsDetailsExamples
            language={application.language}
            hideDetectionText={true}
          />
        )
      }
    />
  )
}

export const AttackEventDetailsCommandInjectionProcessHardening = (
  props: AttackEventDetailsCommandInjectionProps
) => {
  return (
    <AttackEventDetailsCommandInjectionWrapper
      {...props}
      WrappedExamplesComponent={() => (
        <ProcessHardeningDetailsExamples details={props.details} />
      )}
    />
  )
}
